import { Link } from 'react-router-dom';

import {
  Container,
  EmptyState,
  ErrorState,
  LoadingState,
  Section,
  SectionHeading,
} from '@/components/common';
import { routes } from '@/config/routes';
import { CategoryGrid, ProductGrid } from '@/features/catalog/components';
import {
  mapPublicCategoryToCategoryCardModel,
  mapPublicProductSummaryToProductCardModel,
} from '@/features/catalog/api/catalogMappers';
import { useCatalog } from '@/features/catalog/context';
import { useFeaturedProducts, usePublicCategories } from '@/features/catalog/hooks';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

const howItWorks = [
  ['Choose a product', 'Start with wood, an ornament, or apparel that fits the moment.'],
  ['Add your design', 'Bring names, photos, artwork, and the personal details that matter.'],
  ['Review and order', 'Confirm every detail before your personalized piece moves forward.'],
  ['We create and ship it', 'A production partner prepares your order and keeps progress visible.'],
];

const benefits = [
  ['pi pi-heart', 'Personal designs', 'Built around your people, milestones, artwork, and ideas.'],
  [
    'pi pi-eye',
    'Production-ready previews',
    'A clear review flow is planned before an order enters production.',
  ],
  [
    'pi pi-verified',
    'Quality-focused fulfillment',
    'Product and workshop workflows will keep handoffs intentional.',
  ],
  [
    'pi pi-refresh',
    'Easy repeat ordering',
    'Saved designs and identifiers will make favorites easier to order again.',
  ],
];

const occasions = [
  'Weddings',
  'New babies',
  'Holidays',
  'Family memories',
  'Small businesses',
  'Special events',
];

export function HomePage() {
  useDocumentMetadata(
    'Personalized Gifts',
    'Personalized wood engraving, ornaments, and apparel made for meaningful moments.',
  );
  const { media } = useCatalog();
  const categoriesQuery = usePublicCategories({ limit: 12 });
  const featuredQuery = useFeaturedProducts({ limit: 6 });
  const categories = (categoriesQuery.data?.items ?? []).map((category) =>
    mapPublicCategoryToCategoryCardModel(category, media),
  );
  const featured = (featuredQuery.data?.items ?? []).map((product) =>
    mapPublicProductSummaryToProductCardModel(product, media),
  );

  return (
    <>
      <Section className="home-hero" spacing="large">
        <Container className="home-hero__grid" size="wide">
          <div className="home-hero__content">
            <p className="eyebrow">Made for your people and moments</p>
            <h1>
              Made personal.
              <br />
              Remembered forever.
            </h1>
            <p className="lead">
              Create thoughtful wood engravings, ornaments, and apparel that turn your favorite
              stories into something tangible.
            </p>
            <div className="button-row">
              <Link className="button-link button-link--primary" to={routes.products}>
                Start Creating
              </Link>
              <Link className="button-link button-link--secondary" to={routes.products}>
                Browse Products
              </Link>
            </div>
            <div className="hero-trust">
              <span>
                <i className="pi pi-sparkles" /> Made to personalize
              </span>
              <span>
                <i className="pi pi-refresh" /> Reorder-ready foundation
              </span>
            </div>
          </div>
          <div
            className="hero-composition"
            aria-label="A collection of personalized product examples"
          >
            <img
              className="hero-composition__wood"
              src="/assets/catalog/wood-keepsake.svg"
              alt="Personalized engraved wood keepsake example"
            />
            <img
              className="hero-composition__ornament"
              src="/assets/catalog/ornament-keepsake.svg"
              alt="Personalized holiday ornament example"
            />
            <img
              className="hero-composition__shirt"
              src="/assets/catalog/shirt-keepsake.svg"
              alt="Custom printed T-shirt example"
            />
          </div>
        </Container>
      </Section>

      <Section spacing="large">
        <Container size="wide">
          <SectionHeading
            eyebrow="Find your starting point"
            title="A meaningful canvas for every story"
            description="Choose a category now; guided customization will arrive in the dedicated product milestones."
          />
          {categoriesQuery.isPending && <LoadingState label="Loading product categories" />}
          {categoriesQuery.isError && (
            <ErrorState
              title="Categories are temporarily unavailable"
              description="Featured products and the rest of the page may still be available."
              onRetry={() => void categoriesQuery.refetch()}
            />
          )}
          {categoriesQuery.isSuccess && <CategoryGrid categories={categories} />}
        </Container>
      </Section>

      <Section tone="soft" spacing="large">
        <Container size="wide">
          <SectionHeading
            eyebrow="Customer favorites"
            title="Ideas people come back to"
            description="Explore featured personalized products from the Emazy catalog."
            action={
              <Link className="text-link" to={routes.products}>
                View all products <i className="pi pi-arrow-right" />
              </Link>
            }
          />
          {featuredQuery.isPending && <LoadingState label="Loading featured products" />}
          {featuredQuery.isError && (
            <ErrorState
              title="Featured products are temporarily unavailable"
              description="You can still browse all products while this section reconnects."
              onRetry={() => void featuredQuery.refetch()}
            />
          )}
          {featuredQuery.isSuccess && featured.length > 0 && <ProductGrid products={featured} />}
          {featuredQuery.isSuccess && featured.length === 0 && (
            <EmptyState
              title="No featured products right now"
              description="Browse the full collection to find your starting point."
              actionLabel="Browse all products"
              actionTo={routes.products}
            />
          )}
        </Container>
      </Section>

      <Section spacing="large" id="how-it-works">
        <Container>
          <SectionHeading
            align="center"
            eyebrow="Simple by design"
            title="From your idea to a finished keepsake"
            description="The storefront foundation is ready now. Customization, ordering, and production tools arrive in later milestones."
          />
          <ol className="steps-grid">
            {howItWorks.map(([title, description], index) => (
              <li key={title}>
                <span>{index + 1}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </li>
            ))}
          </ol>
          <div className="centered-action">
            <Link className="button-link button-link--secondary" to={routes.howItWorks}>
              See how Emazy works
            </Link>
          </div>
        </Container>
      </Section>

      <Section tone="dark" spacing="large">
        <Container>
          <SectionHeading
            eyebrow="Thoughtfully connected"
            title="Why build your keepsake with Emazy?"
          />
          <div className="benefit-grid">
            {benefits.map(([icon, title, description]) => (
              <article key={title}>
                <i className={icon} aria-hidden="true" />
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>

      <Section spacing="large">
        <Container>
          <SectionHeading
            align="center"
            eyebrow="Inspiration for real life"
            title="Personal is always in season"
            description="A few places to begin when you know the occasion but not yet the product."
          />
          <div className="occasion-grid">
            {occasions.map((occasion, index) => (
              <article key={occasion}>
                <span>0{index + 1}</span>
                <h3>{occasion}</h3>
                <p>Find a thoughtful way to make this moment unmistakably yours.</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>

      <Section tone="soft" spacing="large">
        <Container className="reorder-panel">
          <div className="reorder-panel__mark" aria-hidden="true">
            <i className="pi pi-qrcode" />
          </div>
          <div>
            <p className="eyebrow">Coming with customer accounts</p>
            <h2>Loved it? Order it again in seconds.</h2>
            <p>
              Future saved designs, product identifiers, and QR or barcode lookup will help you
              return to a favorite without rebuilding it from scratch.
            </p>
            <Link className="text-link" to={routes.howItWorks}>
              Explore the future reorder flow <i className="pi pi-arrow-right" />
            </Link>
          </div>
        </Container>
      </Section>

      <Section spacing="large">
        <Container>
          <SectionHeading
            align="center"
            eyebrow="Experience preview"
            title="The kind of feedback we hope to earn"
            description="These illustrative entries are not verified customer reviews."
          />
          <div className="testimonial-grid">
            {[
              [
                'Thoughtful gift giver',
                '“The preview made it easy to feel confident about every personal detail.”',
              ],
              [
                'Family event organizer',
                '“Returning to a saved group design would make next year so much simpler.”',
              ],
              [
                'Small business owner',
                '“One place for design, ordering, and production status is exactly what our team needs.”',
              ],
            ].map(([persona, quote]) => (
              <figure key={persona}>
                <figcaption>Sample customer feedback · {persona}</figcaption>
                <blockquote>{quote}</blockquote>
              </figure>
            ))}
          </div>
        </Container>
      </Section>

      <Section className="final-cta" spacing="large">
        <Container size="narrow">
          <p className="eyebrow">Your idea starts here</p>
          <h2>Make the next gift feel like it could only be for them.</h2>
          <p>Browse every product concept or begin with the material that feels right.</p>
          <div className="button-row">
            <Link className="button-link button-link--light" to={routes.products}>
              Browse all products
            </Link>
            <Link className="button-link button-link--ghost" to={routes.woodEngraving}>
              Explore wood engraving
            </Link>
          </div>
        </Container>
      </Section>
    </>
  );
}
